<template>
  <div>
    <Head :title="'Paciente: ' + patient.user.name" />
    <div class="shadow_box rounded-lg p-6">
      <h1 class="mb-2 text-xl text-gray-777 font-roboto">Paciente: {{ patient.user.name }}</h1>
      <div class="text-gray-777">{{ patient.user.cpf }}</div>
      <hr class="my-5" />
      <tabs :items="tabItems" itemClass="px-2" @changeTab="changeTab">
        <template #content.timeline>
          <timeline />
        </template>
        <template #content.milestone>
          <milestone />
        </template>
        <template #content.prescription>
          <prescription />
        </template>
        <template #content.certificate>
          <certificate />
        </template>
        <template #content.evolution>
          <evolution />
        </template>
        <template #content.score>
          <score />
        </template>
        <template #content.diet-form>
          <diet-form />
        </template>
        <template #content.health-diary>
          <health-diary />
        </template>
        <template #content.care-plan>
          <care-plan />
        </template>
        <template #content.online-consultation>
          <online-consultation ref="onlineConsultation" />
        </template>
        <template #content.forms>
          <forms />
        </template>
        <template #content.notifications>
          <notifications />
        </template>
        <template #content.chat>
          <chat />
        </template>
      </tabs>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { Head, usePage } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Tabs from '@/Shared/Tabs/Tabs'
import Timeline from '@/Pages/Dashboard/Patients/Tabs/Timeline'
import Milestone from '@/Pages/Dashboard/Patients/Tabs/Milestone'
import Prescription from '@/Pages/Dashboard/Patients/Tabs/Prescription'
import Certificate from '@/Pages/Dashboard/Patients/Tabs/Certificate'
import Evolution from '@/Pages/Dashboard/Patients/Tabs/Evolution'
import Score from '@/Pages/Dashboard/Patients/Tabs/Score'
import DietForm from '@/Pages/Dashboard/Patients/Tabs/DietForm'
import HealthDiary from '@/Pages/Dashboard/Patients/Tabs/HealthDiary'
import CarePlan from '@/Pages/Dashboard/Patients/Tabs/CarePlan'
import OnlineConsultation from '@/Pages/Dashboard/Patients/Tabs/OnlineConsultation'
import Forms from '@/Pages/Dashboard/Patients/Tabs/Forms'
import Notifications from '@/Pages/Dashboard/Patients/Tabs/Notifications'
import Chat from '@/Pages/Dashboard/Patients/Tabs/Chat'

export default {
  components: {
    Head,
    Tabs,
    Timeline,
    Milestone,
    Prescription,
    Certificate,
    Evolution,
    Score,
    DietForm,
    HealthDiary,
    CarePlan,
    OnlineConsultation,
    Forms,
    Notifications,
    Chat,
  },
  props: {
    patient: Object,
  },
  provide() {
    return {
      patient: this.patient,
      professionals: () => computed(() => this.professionals).value,
      goals: () => computed(() => this.goals).value,
      foods: () => computed(() => this.foods).value,
      care_plan_types: () => computed(() => this.care_plan_types).value,
      care_plan_problems: () => computed(() => this.care_plan_problems).value,
      care_plan_frequencies: () => computed(() => this.care_plan_frequencies).value,
      care_plan_alert_repeat_types: () => computed(() => this.care_plan_alert_repeat_types).value,
      diet_form_meal_meals: () => computed(() => this.diet_form_meal_meals).value,
      diet_form_meal_days: () => computed(() => this.diet_form_meal_days).value,
      diet_form_meal_unitys: () => computed(() => this.diet_form_meal_unitys).value,
      certificate_types: () => computed(() => this.certificate_types).value,
      certificate_cids: () => computed(() => this.certificate_cids).value,
      prescription_pharmaceutical_forms: () => computed(() => this.prescription_pharmaceutical_forms).value,
      prescription_routes_administration: () => computed(() => this.prescription_routes_administration).value,
      prescription_frequencies: () => computed(() => this.prescription_frequencies).value,
      prescription_frequency_types: () => computed(() => this.prescription_frequency_types).value,
      prescription_shifts: () => computed(() => this.prescription_shifts).value,
      vital_datas: () => computed(() => this.vital_datas).value,
      timelines: () => computed(() => this.timelines).value,
    }
  },
  mounted() {
    this.loadOptions();
    if(this.user.role === 2 && this.user.professional.chat) {
      this.tabItems.push({
          idTab: 'chat',
          title: 'Chat',
      });
    }
  },
  data() {
    return {
      professionals: Object,
      goals: Object,
      foods: Object,
      care_plan_types: Object,
      care_plan_problems: Object,
      care_plan_frequencies: Object,
      care_plan_alert_repeat_types: Object,
      diet_form_meal_meals: Object,
      diet_form_meal_days: Object,
      diet_form_meal_unitys: Object,
      certificate_types: Object,
      certificate_cids: Object,
      prescription_pharmaceutical_forms: Object,
      prescription_routes_administration: Object,
      prescription_frequencies: Object,
      prescription_frequency_types: Object,
      prescription_shifts: Object,
      vital_datas: Object,
      timelines: Object,
      tabItems: [
        {
          idTab: 'timeline',
          title: 'Timeline',
        },
        {
          idTab: 'milestone',
          title: 'Milestone',
        },
        {
          idTab: 'prescription',
          title: 'Prescrição',
        },
        {
          idTab: 'certificate',
          title: 'Atestado',
        },
        {
          idTab: 'evolution',
          title: 'Evolução',
        },
        {
          idTab: 'score',
          title: 'Score',
        },
        {
          idTab: 'diet-form',
          title: 'Formulário de Dieta',
        },
        {
          idTab: 'health-diary',
          title: 'E-Saúde Diário',
        },
        {
          idTab: 'care-plan',
          title: 'Plano de Cuidados',
        },
        {
          idTab: 'online-consultation',
          title: 'Consulta Online',
        },
        {
          idTab: 'forms',
          title: 'Formulários',
        },
        {
          idTab: 'notifications',
          title: 'Notificações',
        }
      ]
    }
  },
  layout: Layout,
  methods: {
    async loadOptions() {
      await fetch(
        `/patients/get-options-detail/` + this.patient.id
      ).then(res => {
        res.json().then(json => {
          this.professionals = json.professionals;
          this.goals = json.goals;
          this.foods = json.foods;
          this.care_plan_types = json.care_plan_types;
          this.care_plan_problems = json.care_plan_problems;
          this.care_plan_frequencies = json.care_plan_frequencies;
          this.care_plan_alert_repeat_types = json.care_plan_alert_repeat_types;
          this.diet_form_meal_meals = json.diet_form_meal_meals;
          this.diet_form_meal_days = json.diet_form_meal_days;
          this.diet_form_meal_unitys = json.diet_form_meal_unitys;
          this.certificate_types = json.certificate_types;
          this.certificate_cids = json.certificate_cids;
          this.prescription_pharmaceutical_forms = json.prescription_pharmaceutical_forms;
          this.prescription_routes_administration = json.prescription_routes_administration;
          this.prescription_frequencies = json.prescription_frequencies;
          this.prescription_frequency_types = json.prescription_frequency_types;
          this.prescription_shifts = json.prescription_shifts;
          this.vital_datas = json.vital_datas;
          this.timelines = json.timelines;
        });
      });
    },
    changeTab(changed) {
      if(this.user.role == 2 && changed.value_old === 'online-consultation') {
        //this.$refs.onlineConsultation.page = 'list';
        //this.$refs.onlineConsultation.loadJitsi = false;
      }
    },
  },
  setup() {
    const user = computed(() => usePage().props.value.auth.user)
    return { user }
  },
}
</script>
